/**
 * Success Match — how well one casual's availability fits one job, out of 100.
 *
 * Bill's point scoring. Shorthand used below (his notation):
 *   JS / JE   job start / job end            CS / CE   casual start / casual end
 *   JB / CB   job suburb / the casual's PREFERRED WORK suburb
 *   JP / CP   job postcode / the casual's preferred-suburb postcode
 *
 *   Suburb — 20
 *     CB === JB ......................................... 20
 *     JP's area contains CP (same postcode area) ........ 20
 *     CB !== JB and CP not in JP's area ................. 15   (deduct 5)
 *
 *   Travel — 40   (home base → job suburb, the distance the casual travels)
 *     within the km the casual gave ..................... 40
 *     every km past it .................................. −2
 *
 *   Hours — 40   (the casual has to COVER the job)
 *     CS <= JS and CE >= JE ............................. 40
 *     every hour of the job the casual is NOT there ..... −5
 *
 *   Spare availability either side costs nothing — covering the job is the
 *   whole point and unused hours are not the casual's fault. What is charged
 *   for is the job time they cannot cover: turning up after the job starts,
 *   or leaving before it ends. Part hours round up, and the two ends add.
 *
 *   e.g. casual 8:00–13:00 on a job 7:30–14:00 → 30 min missed at the start
 *   (rounds to 1 h) plus 1 h missed at the end = −10, so 30/40. The offer is
 *   still sent: the casual is asked to widen their hours or decline.
 *
 * The three parts sum to the Success Match total, so the header badges always
 * add up to the headline number.
 *
 * Banding (Bill's grades) applies to a part's own percentage of its maximum,
 * and to the total: green >= 80, amber 70–79, red 0–69.
 */

/* ── time helpers ────────────────────────────────────────────────
   Times are stored as 24h "HH:MM" and typed as "7:00 AM" / "12:30 PM". */
const pad = (n) => String(n).padStart(2, '0');

export const TIME_LABEL_RE = /^(\d{1,2}):([0-5]\d)\s*([AP]\.?M\.?)$/i;

/** "7:00 AM" → "07:00".  Returns '' when the text is not a valid label. */
export const parseTimeLabel = (text) => {
  const m = TIME_LABEL_RE.exec(String(text || '').trim());
  if (!m) return '';
  let h = Number(m[1]);
  if (h < 1 || h > 12) return '';
  const mins = Number(m[2]);
  const pm = m[3][0].toUpperCase() === 'P';
  if (h === 12) h = 0;
  return `${pad(pm ? h + 12 : h)}:${pad(mins)}`;
};

export const isTimeLabel = (text) => parseTimeLabel(text) !== '';

/** "07:00" → minutes since midnight, or null. */
const toMin = (hhmm) => {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/* ── banding ─────────────────────────────────────────────────── */
export const bandFor = (got, of) => {
  const pct = of > 0 ? (got / of) * 100 : 0;
  return pct >= 80 ? 'good' : pct >= 70 ? 'ok' : 'poor';
};

/** Postcode "area" — the first two digits, so 3000 and 3008 share an area. */
const pcArea = (pc) => String(pc || '').trim().slice(0, 2);
const norm = (s) => String(s || '').trim().toLowerCase();

export const SUBURB_MAX = 20;
export const TRAVEL_MAX = 40;
export const HOURS_MAX = 40;
export const DEFAULT_RADIUS_KM = 10;   // Bill's worked example; used when the casual gave none

/**
 * @param {object} i
 * @param {string} i.casualStart  CS, "HH:MM"
 * @param {string} i.casualEnd    CE, "HH:MM"
 * @param {string} i.jobStart     JS, "HH:MM"
 * @param {string} i.jobEnd       JE, "HH:MM"
 * @param {string} i.casualBase   CB — the casual's preferred WORK suburb
 * @param {string} i.casualPostcode CP — that suburb's postcode
 * @param {string} i.jobBase      JB — the job's suburb
 * @param {string} i.jobPostcode  JP
 * @param {number|null} i.km      home base → job suburb, in km
 * @param {number|null} i.radiusKm the km the casual said they would travel
 * @param {string} [i.clash]     set when these hours overlap another live job on
 *                               the day — Hours scores 0 and this is the reason
 * @returns {{pct, scored, total, band, parts: Array}}
 */
export const successMatch = ({
  casualStart, casualEnd, jobStart, jobEnd,
  casualBase, casualPostcode, jobBase, jobPostcode,
  km, radiusKm, clash,
}) => {
  const parts = [];
  const add = (key, label, got, of, note) => {
    const clamped = Math.max(0, Math.min(of, Math.round(got)));
    parts.push({ key, label, got: clamped, of, note, band: bandFor(clamped, of) });
  };

  /* ── Suburb / 20 ── */
  const CB = norm(casualBase);
  const JB = norm(jobBase);
  if (!JB) {
    add('suburb', 'Suburb', 0, SUBURB_MAX, 'no job suburb yet');
  } else if (CB && CB === JB) {
    add('suburb', 'Suburb', SUBURB_MAX, SUBURB_MAX, `job is in ${jobBase}, the casual's base`);
  } else if (pcArea(casualPostcode) && pcArea(casualPostcode) === pcArea(jobPostcode)) {
    add('suburb', 'Suburb', SUBURB_MAX, SUBURB_MAX, `${casualPostcode} is in the ${jobPostcode} area`);
  } else {
    add('suburb', 'Suburb', SUBURB_MAX - 5, SUBURB_MAX,
      `${casualBase || 'base'} ${casualPostcode || ''} is outside ${jobBase} ${jobPostcode || ''}`.replace(/\s+/g, ' ').trim());
  }

  /* ── Travel / 40 ── */
  const limit = radiusKm != null && radiusKm > 0 ? radiusKm : DEFAULT_RADIUS_KM;
  if (km == null) {
    add('travel', 'Travel', 0, TRAVEL_MAX, 'distance not known yet');
  } else if (km <= limit) {
    add('travel', 'Travel', TRAVEL_MAX, TRAVEL_MAX, `${km} km, within ${limit} km`);
  } else {
    const over = Math.ceil(km - limit);
    add('travel', 'Travel', TRAVEL_MAX - 2 * over, TRAVEL_MAX, `${km} km — ${over} km past ${limit} km, −${2 * over}`);
  }

  /* ── Hours / 40 ── */
  const CS = toMin(casualStart), CE = toMin(casualEnd);
  const JS = toMin(jobStart), JE = toMin(jobEnd);
  if (clash) {
    /* the casual cannot be in two places at once — no hours are earnable */
    add('hours', 'Hours', 0, HOURS_MAX, clash);
  } else if (CS == null || CE == null || JS == null || JE == null) {
    add('hours', 'Hours', 0, HOURS_MAX, 'job or casual times missing');
  } else if (CS <= JS && CE >= JE) {
    add('hours', 'Hours', HOURS_MAX, HOURS_MAX, 'the casual covers the whole job');
  } else {
    let got = HOURS_MAX;
    const why = [];
    if (CS > JS) {
      const hrs = Math.ceil((CS - JS) / 60);
      got -= 5 * hrs;
      why.push(`starts ${hrs} h after the job begins, −${5 * hrs}`);
    }
    if (CE < JE) {
      const hrs = Math.ceil((JE - CE) / 60);
      got -= 5 * hrs;
      why.push(`leaves ${hrs} h before the job ends, −${5 * hrs}`);
    }
    add('hours', 'Hours', got, HOURS_MAX, why.join(' · ') || 'does not cover the job');
  }

  const scored = parts.reduce((n, x) => n + x.got, 0);
  const total = parts.reduce((n, x) => n + x.of, 0);
  const pct = total ? Math.round((scored / total) * 100) : 0;
  return { pct, scored, total, band: bandFor(scored, total), parts };
};

export default successMatch;
